/**
 * ADR-0186 — door retrieval counters shared by the Tenant Knowledge and
 * Platform Knowledge index tables.
 *
 * Honest labeling is part of the contract: these are retrieval counts through
 * external connections, never effectiveness — and in-app AI serving is not
 * counted, which the zero state says at the point of the zero (owner ruling,
 * option c).
 */

/** One entry's usage summary; null on a surface = the aggregate was unavailable. */
export type EntryUsage = {
  served_in_search: number;
  read_count: number;
  last_served_at: string | null;
  last_read_at: string | null;
};

export const USAGE_HELP = {
  searchHits:
    "Times this entry appeared in search results through an external connection. Counts retrieval, not influence — an entry can be found often and change nothing.",
  reads:
    "Times this entry's full body was fetched through an external connection — the strongest sign a client actually pulled it.",
  lastHit: "Most recent time this entry appeared in search results through an external connection.",
  lastRead: "Most recent time this entry's body was fetched through an external connection.",
  neverCell: "Never through an external connection. In-app AI serving is not counted here.",
  caption: "Usage counts cover external connections only — in-app AI serving is not counted.",
  neverProse: "Never found in search or read through an external connection. In-app AI serving is not counted.",
};

/** Usage count cell: bare number, "—" when usage is unavailable. */
export function usageCountCell(usage: EntryUsage | null | undefined, value: number | undefined) {
  if (!usage) return <td className="muted">—</td>;
  return <td>{value ?? 0}</td>;
}

/** Usage timestamp cell: absolute time (house rule), or a muted "Never" carrying the disclosure. */
export function usageTimeCell(usage: EntryUsage | null | undefined, iso: string | null | undefined) {
  if (!usage) return <td className="muted">—</td>;
  if (!iso) return <td className="muted" title={USAGE_HELP.neverCell}>Never</td>;
  return <td style={{ whiteSpace: "nowrap", fontSize: 12 }}>{new Date(iso).toLocaleString()}</td>;
}

/** One-line usage prose for entry detail pages (absolute timestamps). */
export function usageProse(usage: EntryUsage): string {
  if (usage.served_in_search === 0 && usage.read_count === 0) return USAGE_HELP.neverProse;
  const fmt = (iso: string | null) => (iso ? ` (last ${new Date(iso).toLocaleString()})` : "");
  const times = (n: number) => `${n} ${n === 1 ? "time" : "times"}`;
  return `Found in search ${times(usage.served_in_search)}${fmt(usage.last_served_at)} · Read ${times(usage.read_count)}${fmt(usage.last_read_at)}`;
}

/** Timestamp sort value: null ("never") = -Infinity, so dead entries sink in desc and surface in asc. */
export function usageTimeSortValue(iso: string | null | undefined): number {
  if (!iso) return -Infinity;
  const t = Date.parse(iso);
  return Number.isFinite(t) ? t : -Infinity;
}
